import { useState } from 'react';
import { MessageSquare, Lock, Send, Trash2, Users, CalendarDays } from 'lucide-react';
import { ModuleHero } from '../components/ModuleHero';
import { useActiveRole } from '../state/RoleContext';
import { useStore } from '../hooks/useStore';
import { getDb } from '../services/store';
import {
  listLockerRoomMessages,
  postLockerMessage,
  deleteLockerMessage,
} from '../services/communicationHubService';
import { canViewLockerRoom } from '../permissions/permissions';

export function LockerRoomPage() {
  const { profile } = useActiveRole();
  useStore((d) => d);

  const db = getDb();

  // Find teams the user has access to: captain or has a roster entry → matched profile.
  const myTeams = db.teams.filter((t) => {
    if (t.captainProfileId === profile.id) return true;
    const player = db.players.find((p) => p.profileId === profile.id);
    if (!player) return false;
    return db.rosterEntries.some((r) => r.teamId === t.id && r.playerId === player.id);
  });
  const fallbackTeams = profile.globalRoles.includes('admin') ? db.teams : myTeams;
  const accessibleTeams = fallbackTeams.length > 0 ? fallbackTeams : myTeams;

  const [teamId, setTeamId] = useState<string>(accessibleTeams[0]?.id ?? '');
  const team = db.teams.find((t) => t.id === teamId);
  const [body, setBody] = useState('');

  const messages = team ? listLockerRoomMessages(team.tournamentId, team.id) : [];
  const roster = team ? db.rosterEntries.filter((r) => r.teamId === team.id) : [];
  const games = team
    ? db.games.filter((g) => g.homeTeamId === team.id || g.awayTeamId === team.id)
    : [];
  const slotById = new Map(db.scheduleSlots.map((s) => [s.id, s]));

  const allowed = team
    ? canViewLockerRoom(profile, { tournamentId: team.tournamentId, teamId: team.id }, { roleAssignments: db.roleAssignments }) ||
      profile.globalRoles.includes('admin') ||
      team.captainProfileId === profile.id
    : false;

  const post = () => {
    if (!team || !body.trim()) return;
    postLockerMessage({
      tournamentId: team.tournamentId,
      teamId: team.id,
      authorProfileId: profile.id,
      body,
    });
    setBody('');
  };

  return (
    <>
      <ModuleHero
        eyebrow="Experience"
        title="The Locker Room."
        description="The private space behind the bench. Strategy, jersey numbers, who's bringing the cooler. Members only."
        icon={MessageSquare}
        accent="ice"
        status={team ? { label: team.name, tone: allowed ? 'ok' : 'info' } : undefined}
      >
        {accessibleTeams.length > 1 && (
          <select className="select" value={teamId} onChange={(e) => setTeamId(e.target.value)} style={{ maxWidth: 240 }}>
            {accessibleTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </ModuleHero>

      {!team ? (
        <div className="empty">No team accessible to this profile.</div>
      ) : !allowed ? (
        <div className="card card--glass" style={{ textAlign: 'center' }}>
          <Lock size={20} style={{ marginBottom: 8 }} />
          <h3 className="card__title">Access restricted</h3>
          <p className="muted">Only this team's captain, players, and admins can post here.</p>
        </div>
      ) : (
        <div className="grid grid--2">
          <div className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">{team.name} — channel</h3>
                <p className="card__sub">{messages.length} message{messages.length === 1 ? '' : 's'}</p>
              </div>
            </div>
            <div className="stack" style={{ marginBottom: 14 }}>
              {messages.length === 0 && <div className="muted">No messages yet — start the conversation.</div>}
              {messages.map((m) => {
                const author = db.profiles.find((p) => p.id === m.authorProfileId);
                const mine = m.authorProfileId === profile.id;
                return (
                  <div key={m.id} className="card card--glass" style={{ padding: 12 }}>
                    <div className="row row--between">
                      <div style={{ fontWeight: 600 }}>{author?.displayName ?? 'Unknown'}</div>
                      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                        <span className="muted" style={{ fontSize: 11 }}>{new Date(m.createdAt).toLocaleString()}</span>
                        {mine && (
                          <button className="btn btn--ghost btn--sm" onClick={() => deleteLockerMessage(m.id)} aria-label="Delete">
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    <p style={{ margin: '6px 0 0' }}>{m.body}</p>
                  </div>
                );
              })}
            </div>
            <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <textarea
                className="textarea"
                rows={2}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Talk to the team…"
                style={{ flex: 1 }}
              />
              <button className="btn btn--primary" disabled={!body.trim()} onClick={post}>
                <Send size={14} /> Send
              </button>
            </div>
          </div>

          <div className="stack">
            <div className="card">
              <div className="card__head">
                <h3 className="card__title">
                  <Users size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
                  Roster
                </h3>
              </div>
              {roster.length === 0 ? (
                <div className="empty">No players yet.</div>
              ) : (
                <div className="stack">
                  {roster.slice(0, 12).map((r) => {
                    const player = db.players.find((p) => p.id === r.playerId);
                    const profileRow = player ? db.profiles.find((p) => p.id === player.profileId) : null;
                    return (
                      <div key={r.id} className="row row--between" style={{ padding: '4px 0' }}>
                        <span><strong className="mono">#{r.jerseyNumber}</strong> {profileRow?.displayName ?? 'Unknown'}</span>
                        <span className={`chip chip--${r.status === 'confirmed' ? 'ok' : 'warn'}`}>{r.status}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="card">
              <div className="card__head">
                <h3 className="card__title">
                  <CalendarDays size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
                  Schedule
                </h3>
              </div>
              {games.length === 0 ? (
                <div className="empty">No games scheduled.</div>
              ) : (
                <div className="stack">
                  {games.map((g) => {
                    const slot = g.scheduleSlotId ? slotById.get(g.scheduleSlotId) : null;
                    const opp = g.homeTeamId === team.id
                      ? db.teams.find((t) => t.id === g.awayTeamId)
                      : db.teams.find((t) => t.id === g.homeTeamId);
                    return (
                      <div key={g.id} className="row row--between" style={{ padding: '6px 0', borderBottom: '1px solid var(--surface-line)' }}>
                        <div>
                          <strong>{opp?.name ?? 'TBD'}</strong>
                          <div className="muted" style={{ fontSize: 12 }}>{slot ? new Date(slot.startsAt).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '—'}</div>
                        </div>
                        <span className="chip">{g.status.replace('_', ' ')}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
